import { lazy, Suspense, useEffect } from 'react'
import { BrowserRouter, HashRouter, Routes, Route, Navigate, useParams, useLocation } from 'react-router-dom'
import { titleForPath } from './lib/pageTitles'
import { Capacitor } from '@capacitor/core'
import { isInstalledApp } from './lib/platform'
import { AuthProvider } from './contexts/AuthContext'
import { UserProvider } from './contexts/UserContext'
import { NotificationProvider } from './contexts/NotificationContext'
import { AppNotificationsProvider } from './contexts/AppNotificationsContext'
import { Toaster } from 'sonner'
import { QueryClientProvider } from '@tanstack/react-query'
import { AppUpdate } from './components/AppUpdate'
import PerchWidget from './components/PerchWidget'
import ErrorBoundary from './components/ErrorBoundary'
import ProtectedRoute from './components/ProtectedRoute'
import { queryClient } from './lib/queryClient'
import Login from './pages/Login'
import Register from './pages/Register'
import ResetPassword from './pages/ResetPassword'
import TournamentView from './pages/TournamentView'
import BracketPicks from './pages/BracketPicks'
import Leaderboards from './pages/Leaderboards'
import Groups from './pages/Groups'
import GroupDetail from './pages/GroupDetail'
import JoinGroup from './pages/JoinGroup'
import Profile from './pages/Profile'
import Prizes from './pages/Prizes'
import Home from './pages/Home'
import ShopLanding from './pages/ShopLanding'

// Merch storefront — code-split so the tournament app doesn't pay for it.
const StoreApp = lazy(() => import('./store/StoreApp'))

// Legal pages — code-split too; their ported text is heavy and rarely read.
const LegalPage = lazy(() => import('./pages/legal/LegalPage'))

// Contact form — code-split so the Turnstile widget only loads for visitors
// who actually open it.
const ContactPage = lazy(() => import('./pages/ContactPage'))
const UnsubscribedPage = lazy(() => import('./pages/UnsubscribedPage'))
const GroupEmailStatusPage = lazy(() => import('./pages/GroupEmailStatusPage'))
const ConfirmSubscriptionPage = lazy(() => import('./pages/ConfirmSubscriptionPage'))

// Admin Pages
import AdminDashboard from './pages/admin/AdminDashboard'
import TournamentManagement from './pages/admin/TournamentManagement'
import TournamentCreate from './pages/admin/TournamentCreate'
import TournamentEdit from './pages/admin/TournamentEdit'
import BracketPopulation from './pages/admin/BracketPopulation'
import BracketRounds from './pages/admin/BracketRounds'
import TournamentPublished from './pages/admin/TournamentPublished'
import PlayerManagement from './pages/admin/PlayerManagement'
import SportsManagement from './pages/admin/SportsManagement'
import UserManagement from './pages/admin/UserManagement'
import GroupManagement from './pages/admin/GroupManagement'
import PrizeManagement from './pages/admin/PrizeManagement'
import DiscountManagement from './pages/admin/DiscountManagement'
import UserSettings from './pages/Settings'
import DevCardStates from './pages/DevCardStates'
import DevBracket from './pages/DevBracket'

function BracketDefaultRedirect() {
  const { bracketId } = useParams()
  return <Navigate to={`/bracket/${bracketId}/picks`} replace />
}

// Legacy shop.getdub.com paths from before the storefront moved into this app.
function LegacyProductRedirect() {
  const { handle } = useParams()
  return <Navigate to={`/merch/products/${handle}`} replace />
}

// The storefront briefly lived at /store before settling at /merch.
function LegacyStoreRedirect() {
  const { pathname, search } = useLocation()
  return <Navigate to={pathname.replace(/^\/store/, '/merch') + search} replace />
}

// Discount share links (getdub.com/discount/CODE) — the headless stand-in for
// Shopify's online-store /discount links. Stash the code where the store's
// cart looks for it (key mirrored in store/context/CartContext.jsx, not
// imported so the lazy store chunk stays out of the main bundle), then land
// on the storefront; the code is applied to the Shopify cart at checkout.
function DiscountRedirect() {
  const { code } = useParams()
  try { localStorage.setItem('dub-store-discount', code) } catch { /* ignore */ }
  return <Navigate to="/merch" replace />
}

// Matches the storefront's own background so the lazy-chunk load doesn't flash
// white before store.css arrives.
function StoreLoading() {
  return <div className="min-h-screen" style={{ background: '#14101f' }} />
}

// On the web the root path is the public marketing homepage — this build now
// serves getdub.com itself, not just the app. Signed-in users reach the app
// via the landing page's App link (/login forwards them straight to /home).
//
// App contexts skip marketing and open on the dashboard instead:
// - installed apps (Capacitor native builds and home-screen PWAs, whose
//   cached start_url is '/' — see isInstalledApp)
// - anything served on the app.* subdomain (app.getdub.com is the app's
//   domain; also covers existing PWA installs pinned from it)
function RootRoute() {
  const hostname = typeof window !== 'undefined' ? window.location.hostname : ''
  if (isInstalledApp() || hostname.startsWith('app.')) {
    return (
      <ProtectedRoute>
        <Navigate to="/home" replace />
      </ProtectedRoute>
    )
  }
  return <ShopLanding />
}

// Keeps the document title in step with the route. Screen readers announce it
// on navigation, so in a single-page app nothing else tells a non-sighted user
// the page changed. Lives inside the Router so it can read the location.
function DocumentTitle() {
  const { pathname } = useLocation()
  useEffect(() => {
    document.title = titleForPath(pathname)
  }, [pathname])
  return null
}

function App() {
  const Router = Capacitor.isNativePlatform() ? HashRouter : BrowserRouter

  return (
    <ErrorBoundary>
    <QueryClientProvider client={queryClient}>
    <AuthProvider>
      <UserProvider>
        <AppNotificationsProvider>
          <NotificationProvider>
            <Toaster richColors position="top-right" />
            <AppUpdate />
            <PerchWidget />
            <Router>
              <DocumentTitle />
              <Routes>
                <Route path="/" element={<RootRoute />} />
                <Route path="/shop" element={<ShopLanding />} />

                {/* Merch storefront (public) + legacy /store and shop.getdub.com paths */}
                <Route path="/merch/*" element={<Suspense fallback={<StoreLoading />}><StoreApp /></Suspense>} />
                <Route path="/store/*" element={<LegacyStoreRedirect />} />
                <Route path="/discount/:code" element={<DiscountRedirect />} />
                <Route path="/custom" element={<Navigate to="/merch/custom" replace />} />
                <Route path="/products/:handle" element={<LegacyProductRedirect />} />

                {/* Legal pages (public) + the Shopify-era getdub.com URLs for them */}
                <Route path="/terms" element={<Suspense fallback={null}><LegalPage page="terms" /></Suspense>} />
                <Route path="/privacy" element={<Suspense fallback={null}><LegalPage page="privacy" /></Suspense>} />
                <Route path="/pages/terms-of-service" element={<Navigate to="/terms" replace />} />
                <Route path="/pages/privacy-policy" element={<Navigate to="/privacy" replace />} />

                {/* Contact form (public) */}
                <Route path="/contact" element={<Suspense fallback={null}><ContactPage /></Suspense>} />

                {/* Where crm-unsubscribe lands a recipient after removing them.
                    Public and unauthenticated by necessity — nobody should need
                    an account to stop receiving email. */}
                <Route path="/unsubscribed" element={<Suspense fallback={null}><UnsubscribedPage /></Suspense>} />
                {/* Landing for the Stop / Report links in group owners' emails */}
                <Route path="/group-email" element={<Suspense fallback={null}><GroupEmailStatusPage /></Suspense>} />

                {/* Where the newsletter confirmation link lands. Confirms on a
                    button press, not on load: mail scanners open every link. */}
                <Route path="/confirm-subscription" element={<Suspense fallback={null}><ConfirmSubscriptionPage /></Suspense>} />
                <Route path="/login" element={<Login />} />
                <Route path="/register" element={<Register />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                
                {/* Protected Routes */}
                <Route path="/home" element={<ProtectedRoute><Home /></ProtectedRoute>} />
                <Route path="/dashboard" element={<ProtectedRoute><Navigate to="/home" replace /></ProtectedRoute>} />
                <Route path="/tournaments" element={<ProtectedRoute><TournamentManagement /></ProtectedRoute>} />
                <Route path="/tournaments/create" element={<ProtectedRoute requireAdmin><TournamentCreate /></ProtectedRoute>} />
                <Route path="/tournaments/:id" element={<ProtectedRoute><TournamentView /></ProtectedRoute>} />
                <Route path="/brackets/create" element={<ProtectedRoute requireAdmin><BracketPopulation /></ProtectedRoute>} />
                <Route path="/brackets/:bracketId" element={<ProtectedRoute><BracketDefaultRedirect /></ProtectedRoute>} />
                <Route path="/bracket/:bracketId" element={<ProtectedRoute><BracketDefaultRedirect /></ProtectedRoute>} />
                <Route path="/bracket/:bracketId/seed" element={<ProtectedRoute requireAdmin><BracketPopulation /></ProtectedRoute>} />
                <Route path="/bracket/:bracketId/rounds" element={<ProtectedRoute requireAdmin><BracketRounds /></ProtectedRoute>} />
                <Route path="/bracket/:bracketId/picks" element={<ProtectedRoute><BracketPicks /></ProtectedRoute>} />
                <Route path="/leaderboards" element={<ProtectedRoute><Leaderboards /></ProtectedRoute>} />
                <Route path="/groups" element={<ProtectedRoute><Groups /></ProtectedRoute>} />
                <Route path="/groups/:id" element={<ProtectedRoute><GroupDetail /></ProtectedRoute>} />
                <Route path="/groups/:id/:tab" element={<ProtectedRoute><GroupDetail /></ProtectedRoute>} />
                {/* Public: a signed-out invitee sees the group preview first */}
                <Route path="/join/:groupId" element={<JoinGroup />} />
                <Route path="/sports" element={<ProtectedRoute requireAdmin><SportsManagement /></ProtectedRoute>} />
                <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
                <Route path="/profile/:userId" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
                <Route path="/prizes" element={<ProtectedRoute><Prizes /></ProtectedRoute>} />
                
                {/* Admin Routes */}
                <Route path="/admin" element={<ProtectedRoute requireAdmin><Navigate to="/admin/dashboard" replace /></ProtectedRoute>} />
                <Route path="/admin/dashboard" element={<ProtectedRoute requireAdmin><AdminDashboard /></ProtectedRoute>} />
                <Route path="/admin/tournaments" element={<ProtectedRoute requireAdmin><Navigate to="/admin/dashboard?tab=tournaments" replace /></ProtectedRoute>} />
                <Route path="/admin/tournaments/create" element={<ProtectedRoute requireAdmin><Navigate to="/tournaments/create" replace /></ProtectedRoute>} />
                <Route path="/admin/tournaments/:id/edit" element={<ProtectedRoute requireAdmin><TournamentEdit /></ProtectedRoute>} />
                <Route path="/admin/tournaments/:id/published" element={<ProtectedRoute requireAdmin><TournamentPublished /></ProtectedRoute>} />
                <Route path="/admin/players" element={<ProtectedRoute requireAdmin><PlayerManagement /></ProtectedRoute>} />
                <Route path="/admin/users" element={<ProtectedRoute requireAdmin><UserManagement /></ProtectedRoute>} />
                <Route path="/admin/groups" element={<ProtectedRoute requireAdmin><GroupManagement /></ProtectedRoute>} />
                <Route path="/admin/prizes" element={<ProtectedRoute requireAdmin><PrizeManagement /></ProtectedRoute>} />
                <Route path="/admin/discounts" element={<ProtectedRoute requireAdmin><DiscountManagement /></ProtectedRoute>} />
                <Route path="/settings" element={<ProtectedRoute><UserSettings /></ProtectedRoute>} />
                <Route path="/dev/cards" element={<ProtectedRoute requireAdmin><DevCardStates /></ProtectedRoute>} />
                <Route path="/dev/bracket" element={<ProtectedRoute requireAdmin><DevBracket /></ProtectedRoute>} />
              </Routes>
            </Router>
          </NotificationProvider>
        </AppNotificationsProvider>
      </UserProvider>
    </AuthProvider>
    </QueryClientProvider>
    </ErrorBoundary>
  )
}

export default App
